import { decodeHtmlEntities } from 'lib/util';

// Show hosts. When one appears in a show's hour tags or DJs, the show page shows "Hosted By".
// `keys` are loose name keys (see hostNameKey) matched against DJ names, tag names and tag slugs.
// `djSlug` is the host's /dj/<slug> profile, when they have one.
export const SHOW_HOSTS = [
  { name: 'Joanna O.', keys: ['joannao'], djSlug: 'joanna-o' },
  { name: 'Jesse Split', keys: ['jessesplit'], djSlug: 'jessesplit' },
  { name: 'Blair Johnson', keys: ['blairjohnson'] },
];

/**
 * Lowercase letters/digits only, without a leading "DJ": "DJ Jesse Split", "jesse-split" → "jessesplit".
 * @param {string} value
 * @returns {string}
 */
export function hostNameKey(value) {
  return String(value || '')
    .toLowerCase()
    .trim()
    .replace(/^dj\s+/, '')
    .replace(/[^a-z0-9]/g, '');
}

/**
 * Hosts found in a show's DJs and hour tags, in SHOW_HOSTS order. Empty when every hour
 * has the same host(s), e.g. Joanna in Hour 1 and Hour 2: the hour rows already say it.
 * @param {Array} showDjs Parsed `showDjs` (each: name, hour, optional tag_slug, …)
 * @param {Array} showHours Parsed `showHours` (each: hour, djs: [{ name, slug }], tags: [{ name, slug }])
 * @returns {Array<{ name: string, dj: Object|null }>} `dj` is the matching showDjs entry, for the profile modal
 */
export function getShowHosts(showDjs = [], showHours = []) {
  const found = new Set();
  // Host keys per hour label, to spot the same host(s) on every hour
  const byHour = new Map();
  const add = (value, hour) => {
    const key = hostNameKey(value);
    if (!key) return;
    found.add(key);
    if (hour) {
      if (!byHour.has(hour)) byHour.set(hour, new Set());
      byHour.get(hour).add(key);
    }
  };

  showDjs.forEach((dj) => {
    add(dj?.name, dj?.hour);
    add(dj?.tag_slug, dj?.hour);
  });
  showHours.forEach((hour) => {
    [...(hour?.djs || []), ...(hour?.tags || [])].forEach((term) => {
      if (typeof term === 'string') {
        add(term, hour?.hour);
      } else {
        add(term?.name, hour?.hour);
        add(term?.slug, hour?.hour);
      }
    });
  });

  const hosts = SHOW_HOSTS.filter((host) => host.keys.some((key) => found.has(key)));
  const hostsInHour = (keys) =>
    hosts
      .filter((host) => host.keys.some((key) => keys.has(key)))
      .map((h) => h.name)
      .join('|');
  const perHour = [...byHour.values()].map(hostsInHour);
  if (perHour.length >= 2 && perHour[0] && perHour.every((names) => names === perHour[0])) {
    return [];
  }

  return hosts.map((host) => ({
    name: host.name,
    dj:
      showDjs.find(
        (dj) => host.keys.includes(hostNameKey(dj?.name)) || host.keys.includes(hostNameKey(dj?.tag_slug))
      ) || null,
  }));
}

/**
 * Host names from a show's `showHours` (JSON string or parsed array), for listings
 * that don't load `showDjs`. Only hour tags count here.
 * @param {string|Array} showHours
 * @returns {string[]}
 */
export function hostNamesFromShowHours(showHours) {
  return getShowHosts([], parseShowHours(showHours)).map((host) => host.name);
}

/**
 * Each hour's tags as [{ hour, tags: [{ name, slug }] }], DJs first, then the other tags
 * (names decoded: "Drum &amp; Bass" → "Drum & Bass"). Hours without tags are left out.
 * @param {string|Array} showHours
 * @returns {Array<{ hour: string, tags: Array<{ name: string, slug: string }> }>}
 */
export function hourTagsByHour(showHours) {
  return parseShowHours(showHours)
    .map((hour) => ({
      hour: hour?.hour || '',
      tags: [...(hour?.djs || []), ...(hour?.tags || [])]
        .filter((term) => term && typeof term.name === 'string' && term.name)
        .map((term) => ({ name: decodeHtmlEntities(term.name), slug: term.slug || '' })),
    }))
    .filter((hour) => hour.hour && hour.tags.length > 0);
}

// showHours as a JSON string or parsed array → array
function parseShowHours(showHours) {
  if (Array.isArray(showHours)) return showHours;
  if (typeof showHours !== 'string') return [];
  try {
    const parsed = JSON.parse(showHours);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * "A", "A & B", "A, B & C".
 * @param {string[]} names
 * @returns {string}
 */
export function formatHostNames(names = []) {
  if (names.length <= 1) return names[0] || '';
  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
}
